import { ActiveColorFilter } from 'components/common/types/CampaignPlan.types';
import { DirectSalesMediaType } from 'components/common/types/DirectSalesCampaign.types';
import { RouteFrameCode } from 'components/common/types/Deal.types';
import { PlannerMapDataElement } from 'components/common/types/Planner.types';
import {
  ALLOCATED_COLOR_RGB,
  AVAILABLE_COLOR_RGB,
  DISABLE_COLOR_RGB,
  UNAVAILABLE_COLOR_RGB,
  UNAVAILABLE_ORANGE_COLOR_RGB,
} from '../utils/mapColors';

export type AssetColorConfig = {
  isSharedCampaign: boolean;
  isReadOnly: boolean;
  isDirectSales: boolean;
  impressions: { allocatedImpressions?: number; availableImpressions?: number };
  rangeSegments?: { getSegmentRGBColorByIndexValue: (index: number) => number[] | undefined };
  routeFrameCodes: RouteFrameCode[];
  isPriced: boolean;
  activeColorFilter?: ActiveColorFilter | null;
};

type AssetColorData = PlannerMapDataElement & { isAnyFrameInFilter?: boolean };

export type ClusterState = {
  isEverySelected: boolean;
  isPartiallySelected: boolean;
  isEveryMissing: boolean;
  isPartiallyMissing: boolean;
  selectedCount: number;
};

const routeFrameCodesSets = new WeakMap<RouteFrameCode[], Set<string>>();

const getRouteFrameCodesSet = (routeFrameCodes: RouteFrameCode[]): Set<string> => {
  const cachedSet = routeFrameCodesSets.get(routeFrameCodes);
  if (cachedSet) return cachedSet;

  const routeFrameCodesSet = new Set(routeFrameCodes.map((routeFrameCode) => routeFrameCode.code));
  routeFrameCodesSets.set(routeFrameCodes, routeFrameCodesSet);

  return routeFrameCodesSet;
};

export const getClusterState = (data: PlannerMapDataElement, routeFrameCodes: RouteFrameCode[]): ClusterState => {
  if (!routeFrameCodes.length)
    return {
      isEverySelected: false,
      isPartiallySelected: false,
      isEveryMissing: false,
      isPartiallyMissing: false,
      selectedCount: 0,
    };

  const routeFrameCodesSet = getRouteFrameCodesSet(routeFrameCodes);
  if (!data.properties.assets?.length) {
    const isSelected = Boolean(routeFrameCodesSet.has(data.properties.routeFrameCode));
    const isMissing = data.properties.availability === 'Missing';
    return {
      isEverySelected: isSelected,
      isPartiallySelected: false,
      isEveryMissing: isMissing,
      isPartiallyMissing: false,
      selectedCount: isSelected ? 1 : 0,
    };
  }

  const { assetsSet, missingAssets } = data.properties.assets.reduce(
    (acc: { assetsSet: string[]; missingAssets: string[] }, curr) => {
      if (curr.availability === 'Missing') acc.missingAssets.push(curr.routeFrameCode);
      else acc.assetsSet.push(curr.routeFrameCode);

      return acc;
    },
    {
      assetsSet: [],
      missingAssets: [],
    },
  );

  const selected = assetsSet.filter((asset) => routeFrameCodesSet.has(asset)).length;
  const missing = missingAssets.filter((asset) => routeFrameCodesSet.has(asset)).length;

  const isEverySelected = selected === data.properties.assets.length;
  const isEveryMissing = missing === data.properties.assets.length;

  return {
    isEverySelected,
    isPartiallySelected: !isEverySelected && selected > 0,
    isEveryMissing,
    isPartiallyMissing: !isEveryMissing && missing > 0,
    selectedCount: selected,
  };
};

const isDisabledAsset = (config: AssetColorConfig): boolean => {
  const { isReadOnly, isDirectSales, impressions } = config;
  return isReadOnly || (!isDirectSales && !impressions.allocatedImpressions && !impressions.availableImpressions);
};

const isAssetUnavailable = (data: PlannerMapDataElement): boolean => {
  const { availability } = data.properties;
  return Boolean((availability && availability !== 'Available') || (!availability && data.isEveryFrameUnavailable));
};

const findChannelColor = (
  asset: PlannerMapDataElement['properties'],
  colorMap: Map<string, number[]>,
): number[] | undefined => {
  const channel = asset.productFormat?.channel;
  return (
    (asset.channelName && colorMap.get(asset.channelName)) ||
    (channel?.channelName && colorMap.get(channel.channelName)) ||
    (channel?.channelCode && colorMap.get(channel.channelCode)) ||
    undefined
  );
};

const findFormatColor = (
  asset: PlannerMapDataElement['properties'],
  colorMap: Map<string, number[]>,
): number[] | undefined => {
  const format = asset.productFormat;
  return (
    (asset.frameSizeName && colorMap.get(asset.frameSizeName)) ||
    (format?.productFormatName && colorMap.get(format.productFormatName)) ||
    (format?.productFormatCode && colorMap.get(format.productFormatCode)) ||
    undefined
  );
};

const findMediaTypeColor = (
  asset: PlannerMapDataElement['properties'],
  colorMap: Map<string, number[]>,
): number[] | undefined =>
  colorMap.get(asset.productFormat?.digital ? DirectSalesMediaType.DIGITAL : DirectSalesMediaType.PAPER);

const findColorInAssets = (
  assets: PlannerMapDataElement['properties'][],
  colorMap: Map<string, number[]>,
  type: ActiveColorFilter['type'],
): number[] | undefined => {
  if (type === 'mediaType') {
    let result: number[] | undefined;
    assets.some((asset) => {
      result = findMediaTypeColor(asset, colorMap);
      return result;
    });
    return result;
  }

  const finder = type === 'channel' ? findChannelColor : findFormatColor;
  let result: number[] | undefined;
  assets.some((asset) => {
    result = finder(asset, colorMap);
    return result;
  });
  return result;
};

const getSharedCampaignAssetColor = (data: AssetColorData, activeColorFilter?: ActiveColorFilter | null): number[] => {
  if (!data.isAnyFrameInFilter) return DISABLE_COLOR_RGB;
  if (!activeColorFilter) return AVAILABLE_COLOR_RGB;

  const props = data.properties;
  const { type, colorMap } = activeColorFilter;

  if (props.assets?.length) {
    const color = findColorInAssets(props.assets, colorMap, type);
    return color ?? DISABLE_COLOR_RGB;
  }

  if (type === 'mediaType') {
    return findMediaTypeColor(props, colorMap) ?? DISABLE_COLOR_RGB;
  }

  const finder = type === 'channel' ? findChannelColor : findFormatColor;
  return finder(props, colorMap) ?? DISABLE_COLOR_RGB;
};

export const getAssetBaseRgb = (data: AssetColorData, config: AssetColorConfig): number[] => {
  const { isSharedCampaign, rangeSegments, routeFrameCodes, isPriced, activeColorFilter } = config;

  if (isSharedCampaign) {
    return getSharedCampaignAssetColor(data, activeColorFilter);
  }

  if (isDisabledAsset(config)) return DISABLE_COLOR_RGB;

  const rangeColor = rangeSegments?.getSegmentRGBColorByIndexValue(data.index ?? 0);
  if (rangeColor) return rangeColor;

  const { isPartiallyMissing, isEveryMissing, isPartiallySelected } = getClusterState(data, routeFrameCodes);
  if (isPartiallyMissing) return UNAVAILABLE_ORANGE_COLOR_RGB;
  if (isEveryMissing && !isPartiallySelected) return UNAVAILABLE_COLOR_RGB;

  if (isPriced) return ALLOCATED_COLOR_RGB;
  if (isAssetUnavailable(data)) return UNAVAILABLE_COLOR_RGB;

  return AVAILABLE_COLOR_RGB;
};
